/* ============================================
   ChannelSelector — Reusable channel toggle
   Toggles social channels with colored icons.
   ============================================ */
import type { SocialChannel } from '@/lib/types'

interface ChannelSelectorProps {
  selected: SocialChannel[]
  onChange: (channels: SocialChannel[]) => void
  showLabels?: boolean
}

const CHANNEL_CONFIG: Record<SocialChannel, { label: string; color: string; icon: React.ReactNode }> = {
  instagram: {
    label: 'Instagram',
    color: '#E4405F',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5"/>
        <circle cx="12" cy="12" r="5"/>
        <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/>
      </svg>
    ),
  },
  facebook: {
    label: 'Facebook',
    color: '#1877F2',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
      </svg>
    ),
  },
  twitter: {
    label: 'X (Twitter)',
    color: '#000000',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  pinterest: {
    label: 'Pinterest',
    color: '#E60023',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 6c-3 0-5 2.5-5 5.5 0 1.5.5 2.8 1.5 3.5.2.1.4 0 .5-.2l.2-.8c0-.1 0-.2-.1-.3-.4-.5-.6-1.3-.6-2.2 0-2.5 1.9-4.7 4.5-4.7 2.3 0 3.7 1.5 3.7 3.6 0 2.6-1.2 4.8-2.8 4.8-.9 0-1.6-.8-1.4-1.7.3-1.1.8-2.3.8-3.1 0-.7-.4-1.3-1.2-1.3-1 0-1.7 1-1.7 2.3 0 .8.3 1.4.3 1.4l-1.1 4.5c-.3 1.3 0 2.9.1 3 0 .1.1.1.2 0 .1-.1 1.1-1.4 1.5-2.6.1-.4.7-2.6.7-2.6.3.7 1.3 1.2 2.3 1.2 3 0 5.1-2.8 5.1-6.5C18.6 8.2 15.7 6 12 6z"/>
      </svg>
    ),
  },
}

export default function ChannelSelector({ selected, onChange, showLabels = true }: ChannelSelectorProps) {
  const toggle = (channel: SocialChannel) => {
    if (selected.includes(channel)) {
      onChange(selected.filter(c => c !== channel))
    } else {
      onChange([...selected, channel])
    }
  }

  return (
    <div className="sm-channel-selector">
      {(Object.entries(CHANNEL_CONFIG) as [SocialChannel, typeof CHANNEL_CONFIG[SocialChannel]][]).map(([key, cfg]) => {
        const isActive = selected.includes(key)
        return (
          <button
            key={key}
            type="button"
            className={`sm-channel-btn ${isActive ? 'sm-channel-btn--active' : ''}`}
            style={{
              '--channel-color': cfg.color,
              borderColor: isActive ? cfg.color : 'var(--border-color)',
              background: isActive ? `${cfg.color}18` : 'transparent',
              color: isActive ? cfg.color : 'var(--text-secondary)',
            } as React.CSSProperties}
            onClick={() => toggle(key)}
            title={cfg.label}
          >
            <span className="sm-channel-btn__icon">{cfg.icon}</span>
            {showLabels && <span className="sm-channel-btn__label">{cfg.label}</span>}
          </button>
        )
      })}
    </div>
  )
}

export { CHANNEL_CONFIG }
